// frontend/src/pages/Admin.jsx
import { useEffect, useState } from 'react';
import { 
  Typography, 
  Box, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  Tooltip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  TextareaAutosize,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  TablePagination,
  InputAdornment,
  TableSortLabel
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import API_BASE_URL from '../apiConfig';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Admin() {
  const [qrs, setQrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Bulk generation state
  const [bulkGenDialog, setBulkGenDialog] = useState(false);
  const [bulkGenFormData, setBulkGenFormData] = useState({
    domain: 'qr.cudel.in',
    prefix: 'cudel',
    count: 10,
    targetUrlTemplate: 'https://example.com/product/{{i}}'
  });
  const [generatedQRs, setGeneratedQRs] = useState([]);
  const [generationResult, setGenerationResult] = useState(null);

  // Pagination state for QR Stats table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Pagination state for Generated URLs table
  const [genPage, setGenPage] = useState(0);
  const [genRowsPerPage, setGenRowsPerPage] = useState(10);

  // Search filters for QR Stats
  const [filters, setFilters] = useState({
    id: '',
    domain: '',
    location_note: '',
    target_url: '',
    visit_count: ''
  });

  // Search filters for Generated URLs
  const [genFilters, setGenFilters] = useState({
    id: '',
    url: '',
    target_url: ''
  });

  // Sorting state for QR Stats
  const [sortConfig, setSortConfig] = useState({
    key: 'visit_count',
    direction: 'desc' // Default to sort by visit count in descending order
  });

  useEffect(() => {
    loadQRStats();
  }, []);

  const loadQRStats = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/qr-stats`, {
      credentials: 'include' // Include cookies with the request
    })
      .then((res) => res.json())
      .then((data) => {
        setQrs(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching QR statistics:', err);
        setLoading(false);
        showSnackbar('Error fetching QR statistics', 'error');
      });
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleBulkGenDialogOpen = () => {
    setBulkGenDialog(true);
  };

  const handleBulkGenDialogClose = () => {
    setBulkGenDialog(false);
  };

  const handleBulkGenInputChange = (e) => {
    const { name, value } = e.target;
    setBulkGenFormData({
      ...bulkGenFormData,
      [name]: value
    });
  };

  const handleBulkGenSubmit = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bulk-generate-qr`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Include cookies with the request
        body: JSON.stringify(bulkGenFormData)
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to generate QR codes');
      }

      setGeneratedQRs(result.qrCodes);
      setGenerationResult(result);
      showSnackbar(`Successfully generated ${result.generated} QR codes (${result.existing} already existed)`, 'success');
      loadQRStats(); // Refresh the QR stats
    } catch (error) {
      console.error('Error generating QR codes:', error);
      showSnackbar(error.message || 'Error generating QR codes', 'error');
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showSnackbar('Copied to clipboard!', 'success');
    }).catch(err => {
      showSnackbar('Failed to copy: ' + err, 'error');
    });
  };

  const handleDownloadUrls = () => {
    if (!generatedQRs.length) return;
    
    const urlsText = generatedQRs.map(qr => qr.url).join('\n');
    const blob = new Blob([urlsText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${bulkGenFormData.prefix}_urls.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    showSnackbar('URLs downloaded as text file', 'success');
  };

  // Handle filter changes for QR Stats
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    setPage(0); // Reset to first page when filter changes
  };

  // Handle filter changes for Generated URLs
  const handleGenFilterChange = (e) => {
    const { name, value } = e.target;
    setGenFilters({ ...genFilters, [name]: value });
    setGenPage(0); // Reset to first page when filter changes
  };
  
  // Handle pagination changes for QR Stats
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle pagination changes for Generated URLs
  const handleChangeGenPage = (event, newPage) => {
    setGenPage(newPage);
  };

  const handleChangeGenRowsPerPage = (event) => {
    setGenRowsPerPage(parseInt(event.target.value, 10));
    setGenPage(0);
  };

  // Handle sorting for QR Stats
  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });
    setPage(0); // Reset to first page when sorting changes
  };

  // Filter QR codes based on search criteria
  const filteredQRs = qrs.filter(qr => {
    return (
      qr.id?.toLowerCase().includes(filters.id.toLowerCase()) &&
      qr.domain?.toLowerCase().includes(filters.domain.toLowerCase()) &&
      (qr.location_note || '').toLowerCase().includes(filters.location_note.toLowerCase()) &&
      qr.target_url?.toLowerCase().includes(filters.target_url.toLowerCase()) &&
      (qr.visit_count || '').toString().includes(filters.visit_count)
    );
  });

  // Sort QR codes based on sort config
  const sortedQRs = [...filteredQRs].sort((a, b) => {
    // For visit_count, convert to numbers for proper numerical sorting
    if (sortConfig.key === 'visit_count') {
      const aValue = parseInt(a[sortConfig.key] || '0');
      const bValue = parseInt(b[sortConfig.key] || '0');
      
      if (sortConfig.direction === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    }
    
    // For other columns, use string comparison
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });
  
  // Apply pagination for QR Stats
  const paginatedQRs = sortedQRs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Filter generated QR codes based on search criteria
  const filteredGenQRs = generatedQRs.filter(qr => {
    return (
      qr.id?.toLowerCase().includes(genFilters.id.toLowerCase()) &&
      qr.url?.toLowerCase().includes(genFilters.url.toLowerCase()) &&
      qr.target_url?.toLowerCase().includes(genFilters.target_url.toLowerCase())
    );
  });
  
  // Apply pagination for Generated URLs
  const paginatedGenQRs = filteredGenQRs.slice(
    genPage * genRowsPerPage,
    genPage * genRowsPerPage + genRowsPerPage
  );

  return (
    <Box>
      <Tabs value={tabValue} onChange={handleTabChange} aria-label="admin tabs">
        <Tab label="QR Statistics" id="admin-tab-0" aria-controls="admin-tabpanel-0" />
        <Tab label="Bulk QR Generation" id="admin-tab-1" aria-controls="admin-tabpanel-1" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Typography variant="h4" component="h2" gutterBottom>
          QR Code Visit Statistics
        </Typography>
        
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={4}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom>
                  Total QR Codes
                </Typography>
                <Typography variant="h3" component="div" color="primary">
                  {qrs.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom>
                  Total Visits
                </Typography>
                <Typography variant="h3" component="div" color="primary">
                  {qrs.reduce((sum, qr) => sum + (parseInt(qr.visit_count) || 0), 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={2}>
              <CardContent>
                <Typography variant="h5" component="h3" gutterBottom>
                  Active QR Codes
                </Typography>
                <Typography variant="h3" component="div" color="primary">
                  {qrs.filter(qr => parseInt(qr.visit_count) > 0).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Search filters for QR Stats */}
        <TableContainer component={Paper} sx={{ mb: 2, p: 2 }} elevation={2}>
          <Typography variant="h6" component="div" sx={{ mb: 2 }}>
            Search Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="ID"
                name="id"
                value={filters.id}
                onChange={handleFilterChange}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="Domain"
                name="domain"
                value={filters.domain}
                onChange={handleFilterChange}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="Location"
                name="location_note"
                value={filters.location_note}
                onChange={handleFilterChange}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="Target URL"
                name="target_url"
                value={filters.target_url}
                onChange={handleFilterChange}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                label="Visit Count"
                name="visit_count"
                value={filters.visit_count}
                onChange={handleFilterChange}
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </TableContainer>
        
        {loading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={2}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Domain</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Target URL</TableCell>
                  <TableCell align="center">
                    <TableSortLabel
                      active={sortConfig.key === 'visit_count'}
                      direction={sortConfig.direction}
                      onClick={() => handleSort('visit_count')}
                    >
                      Visit Count
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Last Visit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedQRs.length > 0 ? (
                  paginatedQRs.map((qr) => (
                    <TableRow key={qr.id} hover>
                      <TableCell>{qr.id}</TableCell>
                      <TableCell>{qr.domain}</TableCell>
                      <TableCell>{qr.location_note || '—'}</TableCell>
                      <TableCell>
                        <Tooltip title={qr.target_url}>
                          <span>
                            {qr.target_url.length > 30
                              ? `${qr.target_url.substring(0, 30)}...`
                              : qr.target_url}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={qr.visit_count || '0'} 
                          color={parseInt(qr.visit_count) > 0 ? "primary" : "default"}
                          variant={parseInt(qr.visit_count) > 0 ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        {qr.last_visit 
                          ? new Date(qr.last_visit).toLocaleString() 
                          : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {filteredQRs.length === 0 && qrs.length > 0 ? 'No matching QR codes found' : 'No QR codes found'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredQRs.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Typography variant="h4" component="h2" gutterBottom>
          Bulk QR Code Generation
        </Typography>
        
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Domain"
                name="domain"
                value={bulkGenFormData.domain}
                onChange={handleBulkGenInputChange}
                fullWidth
                margin="normal"
                helperText="e.g., qr.cudel.in"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Prefix"
                name="prefix"
                value={bulkGenFormData.prefix}
                onChange={handleBulkGenInputChange}
                fullWidth
                margin="normal"
                helperText="e.g., cudel (will generate cudel1, cudel2, etc.)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Count"
                name="count"
                type="number"
                value={bulkGenFormData.count}
                onChange={handleBulkGenInputChange}
                fullWidth
                margin="normal"
                inputProps={{ min: 1, max: 1000 }}
                helperText="Number of QR codes to generate (max 1000)"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Target URL Template"
                name="targetUrlTemplate"
                value={bulkGenFormData.targetUrlTemplate}
                onChange={handleBulkGenInputChange}
                fullWidth
                margin="normal"
                helperText="Use {{i}} as placeholder for the number, e.g., https://example.com/product/{{i}}"
              />
            </Grid>
            <Grid item xs={12}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleBulkGenSubmit}
                sx={{ mt: 2 }}
              >
                Generate QR Codes
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {generatedQRs.length > 0 && (
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" component="h3">
                Generated URLs
              </Typography>
              <Box>
                <Button 
                  startIcon={<DownloadIcon />} 
                  variant="outlined"
                  onClick={handleDownloadUrls}
                  sx={{ mr: 1 }}
                >
                  Download URLs
                </Button>
                <Button 
                  startIcon={<ContentCopyIcon />} 
                  variant="outlined"
                  onClick={() => handleCopyToClipboard(generatedQRs.map(qr => qr.url).join('\n'))}
                >
                  Copy All
                </Button>
              </Box>
            </Box>
            
            {/* Search filters for Generated URLs */}
            <TableContainer component={Paper} sx={{ mb: 2, p: 2 }} elevation={1}>
              <Typography variant="h6" component="div" sx={{ mb: 2 }}>
                Search Generated QR Codes
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="QR ID"
                    name="id"
                    value={genFilters.id}
                    onChange={handleGenFilterChange}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="URL"
                    name="url"
                    value={genFilters.url}
                    onChange={handleGenFilterChange}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Target URL"
                    name="target_url"
                    value={genFilters.target_url}
                    onChange={handleGenFilterChange}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </TableContainer>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>QR ID</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Target URL</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedGenQRs.length > 0 ? (
                    paginatedGenQRs.map((qr) => (
                      <TableRow key={qr.id} hover>
                        <TableCell>{qr.id}</TableCell>
                        <TableCell>{qr.url}</TableCell>
                        <TableCell>
                          <Tooltip title={qr.target_url}>
                            <span>
                              {qr.target_url.length > 30
                                ? `${qr.target_url.substring(0, 30)}...`
                                : qr.target_url}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton 
                            size="small" 
                            onClick={() => handleCopyToClipboard(qr.url)}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        {filteredGenQRs.length === 0 && generatedQRs.length > 0 ? 'No matching QR codes found' : 'No QR codes generated'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={filteredGenQRs.length}
                rowsPerPage={genRowsPerPage}
                page={genPage}
                onPageChange={handleChangeGenPage}
                onRowsPerPageChange={handleChangeGenRowsPerPage}
              />
            </TableContainer>
            
            <Box mt={3} p={2} bgcolor="#f5f5f5" borderRadius={1}>
              <Typography variant="h6" gutterBottom>
                Summary
              </Typography>
              <Typography>
                • Total QR codes: {generationResult?.qrCodes.length || generatedQRs.length}
              </Typography>
              <Typography>
                • Newly created: {generationResult?.generated || 0}
              </Typography>
              <Typography>
                • Already existing: {generationResult?.existing || 0}
              </Typography>
            </Box>
          </Paper>
        )}
      </TabPanel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

